import { createHash } from 'crypto';
import { chmodSync, lstatSync, mkdirSync } from 'fs';
import * as os from 'os';
import * as path from 'path';

/** Cluster id used by servers constructed without one (tests, hand-wired servers). */
export const DEFAULT_CLUSTER_ID = 'default';

const ensured = new Set<string>();

/**
 * The directory a cluster's ipc sockets live in: `<os tmpdir>/gotti-<12 hex of
 * sha256(clusterId)>`, mode 0700 and owned by this user. Never the cwd — a
 * socket file in a shared working directory is reachable by whoever can write
 * there, and two clusters sharing one directory collided on port numbers.
 *
 * Refuses (loudly) a path that is a symlink or a directory owned by someone
 * else; tightens the mode of one it owns.
 */
export function ipcSocketDir(clusterId: string | undefined): string {
  const id = clusterId || DEFAULT_CLUSTER_ID;
  const dir = path.join(os.tmpdir(), `gotti-${createHash('sha256').update(id).digest('hex').slice(0, 12)}`);
  if (ensured.has(dir)) return dir;
  mkdirSync(dir, { recursive: true, mode: 0o700 });
  const st = lstatSync(dir);
  if (st.isSymbolicLink() || !st.isDirectory()) {
    throw new Error(`ipc socket directory ${dir} is not a plain directory (symlink?) — refusing to put cluster sockets there.`);
  }
  const uid = typeof process.getuid === 'function' ? process.getuid() : undefined;
  if (uid !== undefined && st.uid !== uid) {
    throw new Error(`ipc socket directory ${dir} is owned by uid ${st.uid}, not this user (${uid}) — refusing to put cluster sockets there.`);
  }
  if ((st.mode & 0o077) !== 0) chmodSync(dir, 0o700);
  ensured.add(dir);
  return dir;
}

/** Unix socket paths are capped near 104 bytes on macOS; stay well under. */
const MAX_SOCKET_PATH = 100;

/** The ipc socket FILE path a publisher at `host:port` in `clusterId` binds. */
export function ipcSocketPath(clusterId: string | undefined, host: string, port: number | string): string {
  const dir = ipcSocketDir(clusterId);
  const readable = path.join(dir, `${String(host).replace(/[^A-Za-z0-9.-]/g, '_')}_${port}`);
  if (readable.length <= MAX_SOCKET_PATH) return readable;
  return path.join(dir, `${createHash('sha256').update(String(host)).digest('hex').slice(0, 10)}_${port}`);
}
